import { HGCollection } from "./types";
// Type-only: recordsView reaches back into common/, so a value import here
// would close the cycle.
import type { CollectionScope } from "@/state/recordsView";

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** "1 photo" / "2 photos" — English default plural, adequate for the nouns used. */
export function pluralize(count: number, noun: string) {
  return `${count} ${noun}${count > 1 ? "s" : ""}`;
}

export function formatDate(
  value: number | string,
  month: "long" | "short" = "long",
) {
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month,
    day: "numeric",
  });
}

/**
 * A collection's children of both kinds, in one list.
 *
 * Explicit children are the links the owner configured; implicit ones are
 * inherited — a collection whose title record is filed in this one is a child of
 * it. Anything walking the hierarchy has to see both, so nowhere concatenates
 * the two arrays by hand.
 *
 * Tolerates the arrays being absent: a backend predating title records omits
 * them entirely.
 */
export function collectionChildIds(collection: HGCollection): string[] {
  return [...collection.child_ids, ...(collection.implicit_child_ids ?? [])];
}

/** The mirror of {@link collectionChildIds}. */
export function collectionParentIds(collection: HGCollection): string[] {
  return [...collection.parent_ids, ...(collection.implicit_parent_ids ?? [])];
}

/**
 * Which collections the records list is currently willing to show, given the
 * toolbar's collection scope.
 *
 * `undefined` means "whatever the server considers in scope" — the whole
 * descendant subtree on a collection page, everything the user owns outside
 * one. Anything asking the server about the *same* records the list shows has
 * to narrow by the same set, or its answer describes a different list.
 */
export function scopedCollectionIds(
  collectionID: string | undefined,
  collection: HGCollection | undefined,
  scope: CollectionScope,
): string[] | undefined {
  if (!collectionID || scope === "all") return undefined;
  if (scope === "children" && collection) {
    return [collectionID, ...collectionChildIds(collection)];
  }
  return [collectionID];
}

/**
 * Every collection below `collectionID`, at any depth, through both kinds of
 * edge.
 *
 * Collections form a DAG rather than a tree — a collection may have several
 * parents — so `seen` keeps a subtree reachable by two paths from being walked
 * (and reported) twice. With implicit edges it does more than that: the graph is
 * genuinely cyclic, because an implicit edge appears as a side effect of filing
 * a record and no single write can see the loop coming. `seen` is the only thing
 * standing between that and an infinite recursion.
 */
export function getCollectionChildrenRecoursively(
  collectionID: string,
  collections: HGCollection[] | Map<string, HGCollection>,
  seen: Set<string> = new Set(),
): string[] {
  // Accepts the index directly so a caller walking every collection — see
  // buildDescendantsMap — builds it once instead of paying a linear scan at
  // every step of every walk.
  const byID = collections instanceof Map ? collections : indexCollections(collections);
  const walk = (id: string): string[] => {
    const collection = byID.get(id);
    if (!collection) return [];
    const childIDs: string[] = [];
    for (const childID of collectionChildIds(collection)) {
      if (seen.has(childID)) continue;
      seen.add(childID);
      childIDs.push(childID);
      childIDs.push(...walk(childID));
    }
    return childIDs;
  };
  return walk(collectionID);
}

/** `id → collection`, for the walks above. */
export function indexCollections(
  collections: HGCollection[],
): Map<string, HGCollection> {
  return new Map(collections.map((collection) => [collection.id, collection]));
}

/**
 * `collection id → itself and every collection below it`.
 *
 * Built once per collection list because the title-record pass asks "is this
 * record anywhere under that collection" for every candidate pair, far too often
 * to walk the DAG each time.
 */
export function buildDescendantsMap(
  collections: HGCollection[],
): Map<string, Set<string>> {
  const byID = indexCollections(collections);
  const map = new Map<string, Set<string>>();
  for (const collection of collections) {
    map.set(
      collection.id,
      new Set([
        collection.id,
        ...getCollectionChildrenRecoursively(collection.id, byID),
      ]),
    );
  }
  return map;
}
